import { $api, fetchClient } from '@/api/typedClient'
import type { components } from '@/types/api/v1'
import type { EnvMode } from '@/utils/envMode'
import type { IServerSideGetRowsRequest } from 'ag-grid-community'

// --- New API types ---

/** A single log entry (individual event within a group) */
export type CrossroadsLog = components['schemas']['CrossroadsLogV2-Output']

/** One row in the overview grid — pre-grouped by the backend */
export type LogOverviewRow = components['schemas']['LogOverviewRow']

/** Full group with all logs for an order */
export type LogGroup = components['schemas']['LogGroup']

/** Group-level metadata (order info + routing) */
export type GroupMetadata = components['schemas']['GroupMetadata']
export type OrderGroupMetadata = components['schemas']['OrderGroupMetadata']
export type OrderKey = components['schemas']['OrderKey']

/** A set-filter dropdown value. The endpoint returns raw stored values; the
 * grid formats them. */
export type LogFilterValue = string | null

export type LoggingOverviewType =
  components['schemas']['LogOverviewFindRequest']['type']

/** Server-side filter on the overview query (issue owner / issue type /
 * checkpoint states). Built from the Filters popover by toOrderLogsFilter. */
export type OrderLogsFilter = components['schemas']['OrderLogsQuery']

export interface LoggingOverviewDateRange {
  from: Date
  to: Date
}

const DEFAULT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/** The backend caps one page at 200 rows. */
export const LOG_OVERVIEW_PAGE_SIZE = 200

/** Pseudo-columns the two header switches ride in on, matched by the backend's
 * custom_filter. */
const HIDE_SILENCED_COL = '_hide_silenced'
const ISSUES_ONLY_COL = '_issues_only'

/** The batch deep link is a server filter rather than a grid filter so it is on
 * the very first request -- a filter model set after the grid mounts misses it. */
const BATCH_ID_COL = 'batch_id'

export interface LogOverviewServerFilters {
  tenantName?: string
  type: LoggingOverviewType
  dateRange?: LoggingOverviewDateRange
  filter: OrderLogsFilter | null
  mode?: EnvMode
  hideSilenced?: boolean
  issuesOnly?: boolean
  batchId?: string | null
}

type OverviewBody = components['schemas']['LogOverviewFindRequest']

function toggleFilter(on: boolean | undefined) {
  return on ? { filterType: 'set', values: ['true'] } : undefined
}

function overviewBody(
  filters: LogOverviewServerFilters,
  search: string,
  request: Partial<IServerSideGetRowsRequest> & { includeCount?: boolean },
): OverviewBody {
  const now = Date.now()
  const filterModel: Record<string, unknown> = {
    ...(request.filterModel ?? {}),
  }
  const hideSilenced = toggleFilter(filters.hideSilenced)
  if (hideSilenced) filterModel[HIDE_SILENCED_COL] = hideSilenced
  const issuesOnly = toggleFilter(filters.issuesOnly)
  if (issuesOnly) filterModel[ISSUES_ONLY_COL] = issuesOnly
  if (filters.batchId) {
    filterModel[BATCH_ID_COL] = {
      filterType: 'text',
      type: 'equals',
      filter: filters.batchId,
    }
  }
  return {
    type: filters.type,
    fromDate:
      filters.dateRange?.from.toISOString() ??
      new Date(now - DEFAULT_WINDOW_MS).toISOString(),
    toDate: filters.dateRange?.to.toISOString() ?? new Date(now).toISOString(),
    filter: filters.filter,
    mode: filters.mode,
    startRow: request.startRow ?? 0,
    endRow: request.endRow ?? LOG_OVERVIEW_PAGE_SIZE,
    sortModel: (request.sortModel ?? []) as OverviewBody['sortModel'],
    filterModel,
    includeCount: request.includeCount ?? true,
    search,
  }
}

function tenantHeader(tenantName?: string) {
  return tenantName ? { 'X-Tenant-Name': tenantName } : undefined
}

/**
 * SSRM datasource fetch for the log overview grids. A plain named function, not
 * a hook -- useServerSideDataSource keys its react-query cache on fetchFn.name.
 */
export async function fetchLogOverview({
  filters,
  search,
  request,
}: {
  filters: LogOverviewServerFilters
  search: string
  request: IServerSideGetRowsRequest & { includeCount?: boolean }
}): Promise<{ rowData: LogOverviewRow[]; rowCount: number }> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/logging/overview',
    {
      params: { header: tenantHeader(filters.tenantName) },
      body: overviewBody(filters, search, request),
    },
  )
  if (error) {
    throw new Error(
      `Failed to fetch logging overview: ${JSON.stringify(error)}`,
    )
  }
  return { rowData: data.rowData, rowCount: data.rowCount }
}

/** Distinct values for one column's set filter. The other active filters ride
 * along so each dropdown narrows with them. */
export async function fetchLogOverviewFilterValues(
  column: string,
  filters: LogOverviewServerFilters,
  filterModel: IServerSideGetRowsRequest['filterModel'],
  search = '',
): Promise<LogFilterValue[]> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/logging/overview/filter_values',
    {
      params: {
        query: { column },
        header: tenantHeader(filters.tenantName),
      },
      body: overviewBody(filters, search, {
        startRow: 0,
        endRow: 0,
        includeCount: false,
        filterModel: filterModel ?? {},
      }),
    },
  )
  if (error) {
    throw new Error(`Failed to fetch filter values: ${JSON.stringify(error)}`)
  }
  return data
}

export function useLogById(logId: string | null | undefined, tenant?: string) {
  return $api.useQuery(
    'get',
    '/crossroads/logging/{log_group_id}',
    {
      params: {
        path: { log_group_id: logId! },
        header: tenant ? { 'X-Tenant-Name': tenant } : undefined,
      },
    },
    {
      enabled: !!logId,
    },
  )
}

export function useAudit(
  logGroupId: string | null | undefined,
  sagaId: string | null | undefined,
  tenant?: string,
) {
  return $api.useQuery(
    'post',
    '/crossroads/logging/audit',
    {
      params: {
        query: { log_group_id: logGroupId!, saga_id: sagaId! },
        header: tenant ? { 'X-Tenant-Name': tenant } : undefined,
      },
    },
    {
      enabled: !!logGroupId && !!sagaId,
    },
  )
}

/** One checkpoint's silencing config: which saga drives it, and that saga's
 * display name. Keyed by checkpoint field name on OrderIntegrationCheckpoints. */
export type SilencingMetadata = components['schemas']['SilencingMetadata']

/**
 * checkpoint -> saga map behind order checkpoint silencing. Static server
 * config, so it's fetched once and never refetched. Several checkpoints can
 * share a saga (`completed` and `status_updates` are both `progress_status`),
 * which is why silencing is expressed in sagas rather than checkpoints.
 */
export function useOrderCheckpointsMetadata() {
  return $api.useQuery(
    'post',
    '/crossroads/logging/order_checkpoints_metadata',
    {},
    { staleTime: Number.POSITIVE_INFINITY },
  )
}
